import { useIsCurrentStudentArchived } from '@/hooks'
import { ChildFieldWithMetaData } from '@/uikit/FieldManagement'
import { State } from '@seiue/ui'
import { Tabs } from 'antd'
import humps from 'humps'
import {
  FieldDomainEnum,
  FieldGroupRes,
  FieldRes,
  InformalReflection,
  TermReq,
} from 'packages/sdks-next/growp'
import { AnyValueObj } from 'packages/types/index'
import { InfoTypeEnumToFormType } from 'packages/utils'
import React from 'react'
import styled from 'styled-components'

import { ActivityBox } from './ActivityBox'
import { OtherInfo } from './OtherInfo'
import { useUserDetailContext } from './context'
import { IConfig } from './types'
import { getOptionLabel, idCardFormConfig } from './utils'

/**
 * 固定信息外的信息容器
 *
 * @param props - 参数
 * @param props.data - 学生信息
 * @param props.isOwner - 如果是本人，【账号、学工号仅允许查看、隐藏不可见的字段组字段】
 * @returns 固定信息外的信息容器
 */
export const OtherInfoCard: React.FC<{
  data: InformalReflection
  isOwner?: boolean
}> = (props) => {
  const { domain, groupsData } = useUserDetailContext()
  const isStudentArchived = useIsCurrentStudentArchived()
  const { data: infoData, isOwner } = props

  const getInitData = () => {
    // 自定义信息
    const customDataList: AnyValueObj = infoData.fields || {}

    // 家庭信息
    const guardiansInfo: AnyValueObj = {}
    infoData.guardians
      ?.sort((guardianA, guardianB) => guardianA.id - guardianB.id)
      .forEach((guardian: AnyValueObj) => {
        let fieldsOutObj = guardian
        if (guardian['fields']) {
          fieldsOutObj = {
            ...guardian,
            ...guardian['fields'],
            fields: undefined,
          }
        }
        const guardianLevel = guardian['guardianLevel']
        for (const key in fieldsOutObj) {
          guardiansInfo[`guardians.${guardianLevel}.${key}`] = fieldsOutObj[key]
        }
      })

    // 参与活动
    const activityInfo: AnyValueObj = {
      activityNames: infoData.activityNames,
      activities: infoData.activities,
    }

    const finalInitData = {
      ...infoData,
      ...customDataList,
      ...guardiansInfo,
      ...activityInfo,
    }

    return finalInitData
  }

  const getProductConf = (children: ChildFieldWithMetaData[]) => {
    return children.map((child: ChildFieldWithMetaData) => {
      const { originLabel } = child.metadata || {}
      const options = child.options
        ? child.options.map((option: TermReq) => {
            return {
              label: getOptionLabel(originLabel || '', option.name || '-'),
              value: [
                'gender',
                'status',
                'idcardType',
                'guardians.0.pupilType',
                'guardians.1.pupilType',
              ].includes(humps.camelize(originLabel || ''))
                ? option.name
                : `${option.id}`,
              disabled: option.deletedAt ? true : false,
            }
          })
        : undefined
      const { maxCount, minCount, fileSizeLimit } = child.metadata || {}
      return {
        type: InfoTypeEnumToFormType.get(child.type),
        label: child.label,
        name: child.name || '',
        options,
        tootip: child.description,
        maxCount,
        minCount,
        fileSizeLimit,
      }
    })
  }

  const getFormConfig = (fields: FieldRes[]): IConfig[] => {
    let configList: IConfig[] = []
    fields
      .filter((field: FieldRes) => {
        const name = field.metadata?.originLabel || field.name || ''
        if (
          [
            FieldDomainEnum.InformalStudent,
            FieldDomainEnum.InformalTeacher,
          ].includes(domain) &&
          name === 'account'
        ) {
          return false
        }
        return true
      })
      .map((field: FieldRes) => {
        // 证件特殊处理
        if (field.label === '证件') {
          configList = [...configList, ...idCardFormConfig]
          return
        }
        const metadata = field?.metadata || {}
        const { maxCount, minCount, fileSizeLimit, originLabel } =
          metadata || {}

        const fieldName = humps.camelize(originLabel || field.name || '')
        // 如果是本人，本人不可编辑字段进行处理
        const disabled =
          isOwner &&
          (['usin'].includes(fieldName) || field.editableFrontend === 0)

        configList.push({
          type: InfoTypeEnumToFormType.get(field.type),
          label: field.label,
          name: fieldName,
          options: field.options?.map((option: TermReq) => {
            return {
              label: getOptionLabel(originLabel || '', option.name || '-'),
              value: [
                'gender',
                'status',
                'idcardType',
                'guardians.0.pupilType',
                'guardians.1.pupilType',
              ].includes(humps.camelize(originLabel || ''))
                ? option.name
                : option.id || '',
              disabled: option.deletedAt ? true : false,
            }
          }),
          tootip: field.description,
          maxCount,
          minCount,
          fileSizeLimit,
          metadata,
          productConf: getProductConf(field.children || []),
          disabled,
        })
      })
    return configList
  }

  const tabsConfig = groupsData
    ?.filter((groupData: FieldGroupRes) => {
      let showAct = true
      if (
        groupData.name === '参与活动' &&
        (infoData.activities === undefined || infoData.activities?.length === 0)
      ) {
        showAct = false
      }

      // 如果是本人，本人可见字段进行处理
      groupData.fields = groupData.fields?.filter(
        (field: FieldRes) => !isOwner || field.isShowFrontend === 1
      )
      // 当前用户是否已归档
      if (isStudentArchived && groupData.name !== '校友信息') {
        showAct = false
      }

      return (
        !['固定信息'].includes(groupData.name) &&
        Array.isArray(groupData.fields) &&
        groupData.fields.length > 0 &&
        showAct
      )
    })
    .map((groupData: FieldGroupRes) => {
      return {
        key: String(groupData.id),
        label: groupData.name,
        children:
          groupData.name !== '参与活动' ? (
            <OtherInfo
              configs={getFormConfig(groupData.fields || [])}
              data={getInitData() || {}}
              groupName={groupData.name}
              initFields={infoData.fields || {}}
            />
          ) : (
            <ActivityBox infoData={infoData} />
          ),
      }
    })

  if (!infoData) return <State.Empty />

  return (
    <TabsStyled
      tabPosition="left"
      items={tabsConfig}
    />
  )
}

const TabsStyled = styled(Tabs)`
  height: 100%;
  .ant-tabs-nav-list {
    width: 160px;
    padding-top: 12px;
    min-height: calc(100vh - 310px);
  }
  .ant-tabs-tab {
    color: ${(props) => props.theme.text._1};
    font-size: 14px;
    height: 40px;
    padding-left: 20px !important;
    margin-top: 8px !important;
  }

  .ant-tabs-tab:hover {
    color: ${(props) => props.theme.brand._1};
  }

  .ant-tabs-tab-active {
    background-color: ${(props) => props.theme.brand._5};
    color: ${(props) => props.theme.brand._1};
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${(props) => props.theme.brand._1};
  }

  .ant-tabs-ink-bar {
    visibility: hidden;
  }

  .ant-tabs-content-holder {
    overflow: auto;
  }
`
